/**
 * @module composables/useResponsive
 * @description 반응형 뷰포트 플래그를 제공하는 모듈 레벨 싱글톤 composable.
 *
 * 뷰포트 표준 (SURINP-191 Option A / SURINP-302 — assets/css/index.css 헤더와 동일):
 *   Mobile  : < 768px            (휴대폰)
 *   Tablet  : 768px ~ 1279px     (모바일 UI 공유)
 *   PC      : >= 1280px          (PC 리디자인, rd-* 체계)
 *
 * 공식 플래그는 2종뿐이다 (SURINP-302 AC-2.3):
 *   - isMobile     : < 768px   (모바일 전용 동작)
 *   - isPcRedesign : >= 1280px (그리드↔카드 · 검색바 · 액션바 전환의 유일 기준)
 *
 * 모든 호출자가 동일한 ref 를 공유하며 resize 리스너는 앱 전체에서 1개만 등록된다
 * (keep-alive 탭 환경에서 인스턴스별 리스너가 누적되던 구조를 대체).
 */
import { ref, computed, readonly } from 'vue'

// ── 브레이크포인트 상수 (Breakpoint Constants) ──

/** 모바일 상한 (px) - 이 값 미만은 모바일 */
const BREAKPOINT_MOBILE = 768
/** PC 리디자인 하한 (px) - 이 값 이상에서 새 PC 디자인 적용 */
const BREAKPOINT_PC_REDESIGN = 1280

// ── 모듈 레벨 싱글톤 상태: 모든 호출자가 동일 ref 공유 ──

// 폭 기준 = document.documentElement.clientWidth (스크롤바 제외) → CSS @media 의 width 와 동일 기준.
// window.innerWidth (스크롤바 포함) 를 쓰면 세로 스크롤바가 있는 1280px 경계에서
// JS 분기가 CSS 보다 ~15px 먼저 켜져 v-if(JS)/스타일(CSS) 이 어긋난다 (SURINP-302 후속).
const windowWidth = ref(document.documentElement.clientWidth)
// 높이 = window.innerHeight — 분기 플래그가 아니라 화면 안 위치 재조정(어시스턴트 패널 등) 전용 (SURINP-842)
const windowHeight = ref(window.innerHeight)
const isMobile = computed(() => windowWidth.value < BREAKPOINT_MOBILE)
const isPcRedesign = computed(() => windowWidth.value >= BREAKPOINT_PC_REDESIGN)

/** 리스너 등록 여부 (앱 수명 동안 1회만 등록) */
let attached = false
/** resize 디바운스 타이머 ID */
let resizeTimer = null

/**
 * resize 이벤트 핸들러 (150ms 디바운스 적용).
 * windowWidth·windowHeight 를 갱신하고, 플래그는 computed 로 파생된다.
 *
 * @returns {void}
 */
const onResize = () => {
  if (resizeTimer) clearTimeout(resizeTimer)
  resizeTimer = setTimeout(() => {
    windowWidth.value = document.documentElement.clientWidth
    windowHeight.value = window.innerHeight
  }, 150)
}

/**
 * 첫 호출 시 1회만 resize 리스너를 등록한다 (지연 등록 — import 부수효과 없음).
 * 싱글톤은 앱 수명과 함께 가므로 컴포넌트 단위 해제는 하지 않는다.
 *
 * @returns {void}
 */
const ensureListener = () => {
  if (attached) return
  window.addEventListener('resize', onResize, { passive: true })
  attached = true
}

// HMR: 모듈 교체 시 이전 모듈의 리스너 해제 — 고아 리스너 누적 방지 (Vue CLI 5 / webpack)
if (typeof module !== 'undefined' && module.hot) {
  module.hot.dispose(() => {
    window.removeEventListener('resize', onResize)
    if (resizeTimer) clearTimeout(resizeTimer)
  })
}

/**
 * 반응형 뷰포트 플래그 2종을 제공하는 composable 입니다.
 * 반환되는 ref 는 전 호출자가 공유하는 싱글톤이며, 컴포넌트 밖(라우터 가드 등)에서도 호출 가능합니다.
 *
 * - isMobile: 창 너비 < 768px
 * - isPcRedesign: 창 너비 >= 1280px (그리드↔카드 전환의 유일 기준)
 * - windowWidth: 현재 창 너비 (px, 읽기 전용)
 * - windowHeight: 현재 창 높이 (px, 읽기 전용 — 분기 기준으로 쓰지 않는다)
 *
 * @returns {{
 *   isMobile: import('vue').ComputedRef<boolean>,
 *   isPcRedesign: import('vue').ComputedRef<boolean>,
 *   windowWidth: Readonly<import('vue').Ref<number>>,
 *   windowHeight: Readonly<import('vue').Ref<number>>
 * }}
 */
export function useResponsive() {
  ensureListener()
  return { isMobile, isPcRedesign, windowWidth: readonly(windowWidth), windowHeight: readonly(windowHeight) }
}
